import { useState } from "react";
import { MessageSquareText } from "lucide-react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  cn,
} from "web-design-system";
import { handleError } from "web-core/utils";
import { useUpdatePortfolioReviewLine } from "creso-core/hooks";
import { PortfolioReviewLine } from "creso-core/types";

export function LineCommentPopover({
  reviewId,
  line,
  readOnly,
}: {
  reviewId: number;
  line: PortfolioReviewLine;
  readOnly: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState(line.comment);
  const { mutate: updateLine, isPending } = useUpdatePortfolioReviewLine();

  const save = () =>
    updateLine(
      { reviewId, lineId: line.id, comment },
      {
        onSuccess: () => setOpen(false),
        onError: (error) => handleError(error, "Could not save the comment"),
      }
    );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setComment(line.comment);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Comment on ${line.source_scheme_name || line.target_scheme_name}`}
          className={cn(line.comment && "text-chart-1")}
        >
          <MessageSquareText />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-80 flex-col gap-3 p-4">
        <span className="text-sm font-semibold">Comment</span>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Why this recommendation? Printed as-is in the report."
          rows={4}
          disabled={readOnly}
        />
        {readOnly ? null : (
          <Button
            size="sm"
            className="self-end"
            onClick={save}
            disabled={isPending || comment === line.comment}
          >
            Save
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
